import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  Calendar, 
  DollarSign, 
  Users, 
  Star,
  CheckCircle,
  Clock,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const BusinessDashboard = ({ businessData }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    revenue: 0,
    sessionsThisMonth: 0,
    totalBookings: 0,
    opportunities: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch bookings, invoices, and opportunities in parallel
      const [bookingsRes, invoicesRes, opportunitiesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/bookings/my-bookings`, { headers }).catch(() => ({ data: { bookings: [] } })),
        axios.get(`${API_BASE_URL}/api/invoices/my-invoices`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API_BASE_URL}/api/opportunities/available`, { headers }).catch(() => ({ data: { opportunities: [] } }))
      ]);

      const bookings = bookingsRes.data.bookings || [];
      const invoices = invoicesRes.data.invoices || [];
      const opportunities = opportunitiesRes.data.opportunities || [];

      // Calculate stats
      const totalRevenue = invoices.reduce((sum, inv) => {
        const amount = parseFloat(inv.total || 0);
        return sum + amount;
      }, 0);

      // Get current month bookings
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const sessionsThisMonth = bookings.filter(b => {
        const bookingDate = new Date(b.date);
        return bookingDate.getMonth() === currentMonth && bookingDate.getFullYear() === currentYear;
      }).length;

      setStats({
        revenue: totalRevenue,
        sessionsThisMonth,
        totalBookings: bookings.length,
        opportunities: opportunities.length
      });

      // Get upcoming bookings (next 3)
      const upcoming = bookings
        .filter(b => b.status?.toLowerCase() === 'confirmed' || b.status?.toLowerCase() === 'pending')
        .slice(0, 3);
      setRecentBookings(upcoming);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  const recentActivities = [
    {
      id: 1,
      type: 'booking',
      client: 'TechCorp Inc.',
      action: 'New booking for Leadership Training',
      amount: '$2,500',
      time: '2 hours ago',
      status: 'confirmed'
    },
    {
      id: 2,
      type: 'payment',
      client: 'Global Solutions',
      action: 'Payment received',
      amount: '$1,800',
      time: '5 hours ago',
      status: 'completed'
    },
    {
      id: 3,
      type: 'opportunity',
      client: 'StartupXYZ',
      action: 'New training opportunity',
      amount: '$3,200',
      time: '1 day ago',
      status: 'pending'
    }
  ];

  const upcomingBookings = [
    {
      id: 1,
      client: 'Microsoft Corp',
      session: 'Executive Leadership Workshop',
      date: 'Tomorrow',
      time: '9:00 AM - 5:00 PM',
      type: 'In-person'
    },
    {
      id: 2,
      client: 'Amazon AWS',
      session: 'Remote Team Management',
      date: 'March 18',
      time: '2:00 PM - 4:00 PM',
      type: 'Virtual'
    },
    {
      id: 3,
      client: 'Google LLC',
      session: 'Innovation Training',
      date: 'March 22',
      time: '10:00 AM - 3:00 PM',
      type: 'Hybrid'
    }
  ];

  const quickStats = [
    {
      label: 'Revenue This Quarter',
      value: loading ? '...' : `$${stats.revenue.toLocaleString()}`,
      change: stats.revenue > 0 ? '+23%' : 'No data',
      icon: TrendingUp,
      color: 'text-green-600',
      route: '/business/revenue',
      description: 'View detailed revenue analytics'
    },
    {
      label: 'Sessions This Month',
      value: loading ? '...' : stats.sessionsThisMonth.toString(),
      change: stats.sessionsThisMonth > 0 ? '+12%' : 'No data',
      icon: Calendar,
      color: 'text-blue-600',
      route: '/business/bookings',
      description: 'View all bookings and sessions'
    },
    {
      label: 'Client Satisfaction',
      value: '4.9/5',
      change: '+0.2',
      icon: Star,
      color: 'text-yellow-600',
      route: '/business/reviews',
      description: 'View client reviews and feedback'
    },
    {
      label: 'New Opportunities',
      value: loading ? '...' : stats.opportunities.toString(),
      change: stats.opportunities > 0 ? 'New' : 'No new',
      icon: AlertCircle,
      color: 'text-purple-600',
      route: '/business/opportunities',
      description: 'View training opportunities'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return variants[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Quick Stats - Clickable Cards */}
      <div className="grid grid-cols-4 gap-6">
        {quickStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index} 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
              onClick={() => navigate(stat.route)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    <p className={`text-sm font-medium mt-1 ${stat.color}`}>
                      {stat.change} from last period
                    </p>
                    <p className="text-xs text-gray-500 mt-2">{stat.description}</p>
                  </div>
                  <Icon className={`w-8 h-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Recent Activities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Activities</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600 hover:text-blue-800"
                onClick={() => navigate('/business/revenue')}
              >
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(activity.status)}
                    <div>
                      <p className="font-medium text-gray-900">{activity.client}</p>
                      <p className="text-sm text-gray-600">{activity.action}</p>
                      <p className="text-xs text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">{activity.amount}</p>
                    <Badge className={getStatusBadge(activity.status)}>
                      {activity.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Bookings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Upcoming Sessions</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600 hover:text-blue-800"
                onClick={() => navigate('/business/bookings')}
              >
                View Calendar
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : recentBookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No upcoming sessions</p>
                </div>
              ) : (
                recentBookings.map((booking) => (
                  <div key={booking.id} className="p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{booking.title}</p>
                        <p className="text-sm text-blue-600 font-medium">{booking.client}</p>
                      </div>
                      <Badge variant="outline">
                        {booking.type}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {booking.date}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {booking.time}
                        </span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate('/business/bookings')}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      <Card>
        <CardHeader>
          <CardTitle>Action Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-red-800">Pending Invoices</p>
                  <p className="text-sm text-red-600">2 invoices need attention</p>
                </div>
                <Badge className="bg-red-500 text-white">2</Badge>
              </div>
              <Button 
                className="w-full mt-3 bg-red-600 hover:bg-red-700"
                onClick={() => navigate('/business/billing')}
              >
                Review Invoices
              </Button>
            </div>
            
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-yellow-800">New Opportunities</p>
                  <p className="text-sm text-yellow-600">{stats.opportunities} training requests</p>
                </div>
                <Badge className="bg-yellow-500 text-white">{stats.opportunities}</Badge>
              </div>
              <Button 
                className="w-full mt-3 bg-yellow-600 hover:bg-yellow-700"
                onClick={() => navigate('/business/opportunities')}
              >
                View Opportunities
              </Button>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-800">Client Messages</p>
                  <p className="text-sm text-green-600">3 unread messages</p>
                </div>
                <Badge className="bg-green-500 text-white">3</Badge>
              </div>
              <Button 
                className="w-full mt-3 bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/business/messages')}
              >
                Read Messages
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessDashboard;